import { useCallback, useEffect, useRef, useState } from "react"

export type SelectionPosition = {
  x: number
  y: number
  width: number
  height: number
}

export type TextSelectionData = {
  text: string
  position: SelectionPosition | null
  isActive: boolean
  showMenu: boolean
  /** True when the selection includes a personal answer (`data-personal-content`). */
  isPersonalContent: boolean
}

function elementFromSelectionNode(node: Node | null): Element | null {
  if (!node) return null
  if (node.nodeType === Node.ELEMENT_NODE) return node as Element
  return node.parentElement
}

function nodeInsidePersonalContent(node: Node | null): boolean {
  return elementFromSelectionNode(node)?.closest('[data-personal-content="true"]') != null
}

const PERSONAL_CONTENT_SELECTOR = '[data-personal-content="true"]'

function isDocumentNode(node: Node): node is Document {
  return node.nodeType === Node.DOCUMENT_NODE
}

/** Element that contains every personal card the range could overlap. */
function personalContentScope(ancestor: Node): ParentNode {
  if (ancestor instanceof Element) return ancestor
  if (ancestor.parentElement) return ancestor.parentElement
  if (isDocumentNode(ancestor)) return ancestor
  return document
}

/** The range includes a personal answer, even when both endpoints sit outside that card. */
function selectionTouchesPersonalContent(selection: Selection | null): boolean {
  if (!selection) return false
  if (
    nodeInsidePersonalContent(selection.anchorNode) ||
    nodeInsidePersonalContent(selection.focusNode)
  ) {
    return true
  }
  if (selection.rangeCount === 0) return false

  let range: Range
  try {
    range = selection.getRangeAt(0)
  } catch {
    return false
  }

  const scope = personalContentScope(range.commonAncestorContainer)
  for (const node of scope.querySelectorAll(PERSONAL_CONTENT_SELECTOR)) {
    try {
      if (range.intersectsNode(node)) return true
    } catch {
      // intersectsNode throws when the node is not in this document, so it is not in the range.
    }
  }
  return false
}

const MIN_SELECTION_LENGTH = 5
const SELECTION_DELAY = 800 // 0.8s delay before showing button
const MENU_DIMENSIONS = {
  WIDTH: 250,
  HEIGHT: 400,
  MARGIN: 8,
} as const

/**
 * Hook to detect and track text selection within elements marked with data-text-selectable
 */
export function useTextSelection(enabled: boolean = true) {
  const [selection, setSelection] = useState<TextSelectionData>({
    text: "",
    position: null,
    isActive: false,
    showMenu: false,
    isPersonalContent: false,
  })
  const timeoutRef = useRef<NodeJS.Timeout | null>(null)

  const clearSelection = useCallback(() => {
    setSelection({
      text: "",
      position: null,
      isActive: false,
      showMenu: false,
      isPersonalContent: false,
    })
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
      timeoutRef.current = null
    }
  }, [])

  const openMenu = useCallback(() => {
    setSelection((prev) => ({ ...prev, showMenu: true }))
  }, [])

  const handleSelectionChange = useCallback(() => {
    if (!enabled) {
      clearSelection()
      return
    }

    // Clear any pending timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
      timeoutRef.current = null
    }

    const windowSelection = window.getSelection()
    const selectedText = windowSelection?.toString().trim() || ""

    // Check if selection meets minimum requirements
    if (!selectedText || selectedText.length < MIN_SELECTION_LENGTH) {
      clearSelection()
      return
    }

    // Check if selection is within an allowed container
    const anchorNode = windowSelection?.anchorNode
    if (!anchorNode) {
      clearSelection()
      return
    }

    // Find the closest parent with data-text-selectable attribute
    let element: HTMLElement | null = null
    if (anchorNode.nodeType === Node.TEXT_NODE) {
      element = anchorNode.parentElement
    } else if (anchorNode.nodeType === Node.ELEMENT_NODE) {
      element = anchorNode as HTMLElement
    }

    const selectableContainer = element?.closest('[data-text-selectable="true"]')
    if (!selectableContainer) {
      clearSelection()
      return
    }

    // Calculate position with delay
    timeoutRef.current = setTimeout(() => {
      try {
        const range = windowSelection.getRangeAt(0)

        const rect = range.getBoundingClientRect()

        // Determine best position based on viewport
        const viewportWidth = window.innerWidth
        const viewportHeight = window.innerHeight

        let x = rect.right + MENU_DIMENSIONS.MARGIN
        let y = rect.top

        // If menu would overflow right side, position it on the left
        if (x + MENU_DIMENSIONS.WIDTH > viewportWidth) {
          x = rect.left - MENU_DIMENSIONS.WIDTH - MENU_DIMENSIONS.MARGIN
        }

        // If menu would overflow bottom, position it above
        if (y + MENU_DIMENSIONS.HEIGHT > viewportHeight) {
          y = rect.bottom - MENU_DIMENSIONS.HEIGHT
        }

        // Ensure menu doesn't go off-screen
        x = Math.max(
          MENU_DIMENSIONS.MARGIN,
          Math.min(x, viewportWidth - MENU_DIMENSIONS.WIDTH - MENU_DIMENSIONS.MARGIN)
        )
        y = Math.max(
          MENU_DIMENSIONS.MARGIN,
          Math.min(y, viewportHeight - MENU_DIMENSIONS.HEIGHT - MENU_DIMENSIONS.MARGIN)
        )

        setSelection({
          text: selectedText,
          position: {
            x,
            y,
            width: rect.width,
            height: rect.height,
          },
          isActive: true,
          showMenu: false, // Don't show menu automatically, just the button
          isPersonalContent: selectionTouchesPersonalContent(windowSelection),
        })
      } catch {
        // Ignore errors from invalid ranges
        clearSelection()
      }
    }, SELECTION_DELAY)
  }, [enabled, clearSelection])

  useEffect(() => {
    if (!enabled) {
      clearSelection()
      return
    }

    // Listen for selection changes
    document.addEventListener("selectionchange", handleSelectionChange)

    // Listen for clicks to close menu
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement
      // Don't close if clicking inside the menu or on the trigger button
      if (
        target.closest('[data-text-selection-menu="true"]') ||
        target.closest('[data-text-selection-trigger="true"]')
      ) {
        return
      }
      // Close on any other click
      clearSelection()
    }
    document.addEventListener("mousedown", handleClickOutside)

    // Listen for scroll to reposition or close
    const handleScroll = () => {
      clearSelection()
    }
    document.addEventListener("scroll", handleScroll, true)

    return () => {
      document.removeEventListener("selectionchange", handleSelectionChange)
      document.removeEventListener("mousedown", handleClickOutside)
      document.removeEventListener("scroll", handleScroll, true)
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
    }
  }, [enabled, handleSelectionChange, clearSelection])

  return {
    ...selection,
    clearSelection,
    openMenu,
  }
}
